import omit from 'lodash.omit';
import NextSelection from '~/constants/new-cars/NextSelection';
import SectionName from '~/constants/new-cars/SectionName';
import type {
  CustomizeRequest,
  ModelVersionConfigurationResponse,
  ParentModelConfigurationResponse,
  SelectedDoors,
  SelectedModelVersion,
  SelectedTrim,
  SelectedTruckBodyStyle,
  SelectedYear,
  YearConfigurationResponse
} from '~/contracts/new-cars';
import isDoorsResult from '~/utilities/new-cars/isDoorsResult';
import { readNewCarsHistoryState } from '~/utilities/new-cars/newCarsHistoryState';

export type UrlParams = {
  doors?: string;
  driveType?: string;
  engine?: string;
  exterior?: string;
  interior?: string;
  modelVersion?: string;
  packages?: string | string[];
  trim?: string;
  truckBodyStyle?: string;
  year?: string;
};

export type ConfiguratorState = {
  selectedDoors: SelectedDoors | null;
  selectedModelVersion: SelectedModelVersion | null;
  selectedOptions: CustomizeRequest | null;
  selectedTrim: SelectedTrim | null;
  selectedTruckBodyStyle: SelectedTruckBodyStyle | null;
  selectedYear: SelectedYear | null;
};

export type InitialStateArgs = {
  sectionSequence: SectionName[];
  initialModelVersionResponse: ModelVersionConfigurationResponse | null;
  initialParentModelResponse: ParentModelConfigurationResponse;
  initialYearsResponse: YearConfigurationResponse | null;
  urlParams?: UrlParams;
};

export function isConfiguratorState(
  value: unknown
): value is ConfiguratorState {
  return (
    typeof value === 'object' &&
    value !== null &&
    'selectedDoors' in value &&
    'selectedModelVersion' in value &&
    'selectedOptions' in value &&
    'selectedTrim' in value &&
    'selectedTruckBodyStyle' in value &&
    'selectedYear' in value
  );
}

export function resolveYear(
  historyState: ConfiguratorState | null,
  urlParams: UrlParams | undefined,
  initialParentModelResponse: ParentModelConfigurationResponse
): SelectedYear | null {
  if (historyState?.selectedYear) return historyState.selectedYear;

  // When the API skipped the year level, the response results are the next
  // level's options (trims/doors), not year options — don't derive a year.
  if (
    initialParentModelResponse.nextSelection !== '' &&
    initialParentModelResponse.nextSelection !== NextSelection.Year
  ) {
    return null;
  }

  if (urlParams?.year) {
    const yearNum = Number(urlParams.year);
    const yearResult = initialParentModelResponse.results.find(
      result => result.next.year === yearNum
    );

    return yearResult
      ? omit({ ...yearResult, ...yearResult.next }, 'next')
      : null;
  }

  if (initialParentModelResponse.results.length === 1) {
    const yearResult = initialParentModelResponse.results[0];

    return omit({ ...yearResult, ...yearResult.next }, 'next');
  }

  return null;
}

export function resolveModelVersion(
  historyState: ConfiguratorState | null,
  urlParams: UrlParams | undefined,
  initialYearsResponse: YearConfigurationResponse | null,
  sectionSequence: SectionName[]
): SelectedModelVersion | null {
  if (historyState?.selectedModelVersion)
    return historyState.selectedModelVersion;

  if (urlParams?.modelVersion && initialYearsResponse) {
    const result = initialYearsResponse.results.find(
      r => r.next.modelVersion === urlParams.modelVersion
    );

    return result ? omit({ ...result, ...result.next }, 'next') : null;
  }

  if (
    !sectionSequence.includes(SectionName.Doors) &&
    !sectionSequence.includes(SectionName.ModelVersion) &&
    initialYearsResponse?.results[0]
  ) {
    return omit(
      {
        ...initialYearsResponse.results[0],
        ...initialYearsResponse.results[0].next
      },
      'next'
    );
  }

  return null;
}

export function resolveDoors(
  historyState: ConfiguratorState | null,
  urlParams: UrlParams | undefined,
  initialYearsResponse: YearConfigurationResponse | null,
  sectionSequence: SectionName[]
): SelectedDoors | null {
  if (historyState?.selectedDoors) return historyState.selectedDoors;

  const hasDoors =
    initialYearsResponse?.nextSelection === NextSelection.DoorCount;

  if (!hasDoors || !initialYearsResponse) return null;

  if (urlParams?.doors) {
    const doorNum = Number(urlParams.doors);
    const result = initialYearsResponse.results
      .filter(isDoorsResult)
      .find(r => r.next.doorCount === doorNum);

    return result ? omit({ ...result, ...result.next }, 'next') : null;
  }

  if (
    !sectionSequence.includes(SectionName.Doors) &&
    initialYearsResponse.results[0]
  ) {
    return omit(
      {
        ...initialYearsResponse.results[0],
        ...initialYearsResponse.results[0].next
      },
      'next'
    ) as SelectedDoors;
  }

  return null;
}

export function resolveTrim(
  historyState: ConfiguratorState | null,
  urlParams: UrlParams | undefined,
  initialModelVersionResponse: ModelVersionConfigurationResponse | null
): SelectedTrim | null {
  if (historyState?.selectedTrim) return historyState.selectedTrim;

  if (urlParams?.trim) {
    if (initialModelVersionResponse) {
      const result = initialModelVersionResponse.results.find(
        r => r.next.trim === urlParams.trim
      );
      if (result) return omit({ ...result, ...result.next }, 'next');
    }

    return null;
  }

  return null;
}

export function resolveTruckBodyStyle(
  historyState: ConfiguratorState | null
): SelectedTruckBodyStyle | null {
  return historyState?.selectedTruckBodyStyle ?? null;
}

export function resolveOptions(
  historyState: ConfiguratorState | null,
  urlParams: UrlParams | undefined,
  initialModelVersionResponse: ModelVersionConfigurationResponse | null,
  selectedTrim: SelectedTrim | null,
  selectedTruckBodyStyle: SelectedTruckBodyStyle | null = null
): CustomizeRequest | null {
  if (historyState?.selectedOptions) return historyState.selectedOptions;

  if (urlParams?.trim && initialModelVersionResponse) {
    // Only restore selectedOptions when option-level params are present in the URL.
    // The trim param alone identifies which trim was selected — it should not cause
    // selectedOptions to be seeded from result.next defaults (which carry API
    // default values that would pollute the URL on the next replaceState call).
    const hasOptionParams =
      urlParams.driveType ||
      urlParams.engine ||
      urlParams.exterior ||
      urlParams.interior ||
      urlParams.packages ||
      urlParams.truckBodyStyle;

    if (!hasOptionParams) return null;

    const result = initialModelVersionResponse.results.find(
      r => r.next.trim === urlParams.trim
    );

    if (result) {
      const packages = urlParams.packages
        ? [urlParams.packages].flat().filter(Boolean)
        : undefined;

      return {
        ...result.next,
        ...(urlParams.truckBodyStyle
          ? { truckBodyStyle: urlParams.truckBodyStyle }
          : {}),
        ...(urlParams.driveType ? { driveType: urlParams.driveType } : {}),
        ...(urlParams.engine ? { engineCode: urlParams.engine } : {}),
        ...(urlParams.exterior
          ? { exteriorColorCode: urlParams.exterior }
          : {}),
        ...(urlParams.interior
          ? { interiorColorCode: urlParams.interior }
          : {}),
        ...(packages?.length ? { packageOptionCodes: packages } : {})
      };
    }

    return null;
  }

  if (selectedTruckBodyStyle) {
    return { ...selectedTruckBodyStyle };
  }

  if (selectedTrim?.isConfigurationComplete) {
    return { ...selectedTrim };
  }

  return null;
}

export function getInitialState({
  sectionSequence,
  initialModelVersionResponse,
  initialParentModelResponse,
  initialYearsResponse,
  urlParams
}: InitialStateArgs): ConfiguratorState {
  const historyState = readNewCarsHistoryState();

  const selectedYear = resolveYear(
    historyState,
    urlParams,
    initialParentModelResponse
  );
  const selectedModelVersion = resolveModelVersion(
    historyState,
    urlParams,
    initialYearsResponse,
    sectionSequence
  );
  const selectedDoors = selectedModelVersion
    ? null
    : resolveDoors(
        historyState,
        urlParams,
        initialYearsResponse,
        sectionSequence
      );
  const selectedTrim = resolveTrim(
    historyState,
    urlParams,
    initialModelVersionResponse
  );
  const selectedTruckBodyStyle = resolveTruckBodyStyle(historyState);
  const selectedOptions = resolveOptions(
    historyState,
    urlParams,
    initialModelVersionResponse,
    selectedTrim,
    selectedTruckBodyStyle
  );

  return {
    selectedDoors,
    selectedModelVersion,
    selectedOptions,
    selectedTrim,
    selectedTruckBodyStyle,
    selectedYear
  };
}
